import React, { memo } from "react";
import { Tab } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Nav from "react-bootstrap/Nav";
import { getURLGridQueryParams } from "../../../../utils";
import { onSearchFn } from "../../@types";
import { LOCATION } from "../../consts";
import {
  createFilterChangeSelections,
  determineSelectionsFromQP
} from "../../util";
import BulkSearchView from "../quicksearchtabs/BulkSearchView";
import FileSearchView from "../quicksearchtabs/files/FileSearchView";
import SuggestionsView from "../quicksearchtabs/SuggestionsView";
import { IQSModalProps, QuickSearchTabsType } from "./@types/QuickSearchModal";
import NetworkView from "../quicksearchtabs/network/NetworkView";

const QuickSearchTabs: Record<QuickSearchTabsType, string> = {
  files: "files",
  bulk: "bulk",
  suggestions: "suggestions",
  network: "network"
};

const QUICK_SEARCH_TABS_ORDER = [
  QuickSearchTabs.files,
  QuickSearchTabs.network,
  QuickSearchTabs.bulk,
  QuickSearchTabs.suggestions
];

const QUICK_SEARCH_TABS_TITLE = {
  [QuickSearchTabs.files]: "Files",
  [QuickSearchTabs.network]: "Network",
  [QuickSearchTabs.bulk]: "Bulk Search",
  [QuickSearchTabs.suggestions]: "Suggestions"
};

const LOCATION_MAP = {
  [LOCATION.CLOUD]: LOCATION.CLOUD,
  [LOCATION.LOCAL]: LOCATION.LOCAL,
  [LOCATION.NETWORK]: LOCATION.NETWORK
};

const QuickSearchModal = ({
  initialActiveTab,
  show,
  onClose,
  onSearch,
  onBulkSearch,
  onHistoryAndFavoritesSearch,
  onSuggestionsSearch
}: IQSModalProps) => {
  const [activeTab, setActiveTab] = React.useState<string>(
    initialActiveTab ?? QuickSearchTabs.files
  );
  const onSelect = (key: string) => {
    setActiveTab(key);
  };

  const onSearchSubmit = (args: onSearchFn) => {
    onSearch({
      query: args.query,
      selections: args.selections,
      location: args.location ?? LOCATION.LOCAL
    });
    onClose();
  };

  return (
    // xxl size doesn't exist in react-bootstrap, but we have a custom size for it
    <Modal
      show={show}
      onHide={onClose}
      centered
      size={"xxl" as never}
      className={"quick-search-modal"}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Quick Search{" "}
          <span className={"quick-search__title-shortcut-indicator"}>
            [Alt+Q]
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tab.Container
          defaultActiveKey={QuickSearchTabs.files}
          activeKey={activeTab}
          onSelect={onSelect}
        >
          <Nav
            variant={"tabs"}
            className={"nav-tabs--bordered mt-0 quick-search__tabs"}
          >
            {QUICK_SEARCH_TABS_ORDER.map((key: QuickSearchTabsType) => (
              <Nav.Item key={key}>
                <Nav.Link
                  eventKey={QuickSearchTabs[key]}
                  className={"quick-search__tab_link"}
                >
                  {QUICK_SEARCH_TABS_TITLE[key]}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          <Tab.Content className={"quick-search__tab_content rlbs"}>
            <Tab.Pane
              eventKey={QuickSearchTabs.files}
              className={"quick-search__tab_pane"}
            >
              <FileSearchView onSubmit={onSearchSubmit} onClose={onClose} />
            </Tab.Pane>
            <Tab.Pane
              eventKey={QuickSearchTabs.network}
              className={"quick-search__tab_pane"}
            >
              <NetworkView onSubmit={onSearchSubmit} onClose={onClose} />
            </Tab.Pane>
            <Tab.Pane
              eventKey={QuickSearchTabs.bulk}
              className={"quick-search__tab_pane"}
            >
              <BulkSearchView onBulkSearch={onBulkSearch} onClose={onClose} />
            </Tab.Pane>
            <Tab.Pane
              eventKey={QuickSearchTabs.suggestions}
              className={"quick-search__tab_pane"}
            >
              <SuggestionsView
                onClose={onClose}
                onExampleSearch={onSuggestionsSearch || onSearchSubmit}
                onHistoryAndFavoritesSearch={
                  onHistoryAndFavoritesSearch ||
                  (value => {
                    const {
                      searchQuery: query,
                      selections: suggestionSelections,
                      filters: { location: searchLocation }
                    } = getURLGridQueryParams(value.query);
                    const newLocation =
                      LOCATION_MAP[searchLocation] || LOCATION.LOCAL;
                    const historyFavoritesSelections =
                      createFilterChangeSelections(
                        newLocation,
                        determineSelectionsFromQP(suggestionSelections)
                      );
                    onSearchSubmit({
                      query,
                      selections: historyFavoritesSelections,
                      location: newLocation
                    });
                  })
                }
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
    </Modal>
  );
};

export default memo(QuickSearchModal);
